import * as React from 'react';

type SvgPathsProps = {
  paths: string | string[];
  size?: number | string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  decorative?: boolean;
  title?: string;
  viewBox?: string;
  autoBox?: boolean;
  boxPad?: number;
  className?: string;
  role?: string;
  tooltip?: boolean;
};

export default function SvgPaths({
  paths,
  size = 24,
  fill = 'currentColor',
  stroke,
  strokeWidth,
  decorative = false,
  title,
  viewBox = '0 0 1024 1024',
  autoBox = true,
  boxPad = 0,
  className,
  role,
  tooltip,
}: SvgPathsProps) {
  const pathDataArray = Array.isArray(paths) ? paths : [paths];

  const pathRefs = React.useRef<SVGPathElement[]>([]);
  pathRefs.current = [];

  const [computedViewBox, setComputedViewBox] = React.useState<string>(viewBox);

  React.useEffect(() => {
    if (!autoBox) return;
    if (!pathRefs.current.length) return;

    // Use setTimeout to ensure paths are rendered
    setTimeout(() => {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      for (const pathElement of pathRefs.current) {
        try {
          const b = pathElement.getBBox();
          if (b.width > 0 && b.height > 0) {
            minX = Math.min(minX, b.x);
            minY = Math.min(minY, b.y);
            maxX = Math.max(maxX, b.x + b.width);
            maxY = Math.max(maxY, b.y + b.height);
          }
        } catch (error) {
          // getBBox can fail if element isn't rendered yet
        }
      }

      if (minX !== Infinity) {
        const totalWidth = maxX - minX;
        const totalHeight = maxY - minY;
        setComputedViewBox(`${minX - boxPad} ${minY - boxPad} ${totalWidth + boxPad * 2} ${totalHeight + boxPad * 2}`);
      }
    }, 0);
  }, [pathDataArray.join('|'), autoBox, boxPad]);

  const setRef = (el: SVGPathElement | null) => {
    if (el) pathRefs.current.push(el);
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={computedViewBox}
      preserveAspectRatio="xMidYMid meet"
      role={decorative ? undefined : role}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : title || 'icon'}
      style={{ display: tooltip ? 'block' : 'inline-block', overflow: 'visible' }}
    >
      {!decorative && <title>{title || 'icon'}</title>}
      {pathDataArray.map((d, i) => (
        <path className={className} key={i} ref={setRef} d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
      ))}
    </svg>
  );
}
